<template>
  <div class="mt-3">
    <div class="flex items-center justify-between mb-2">
      <h4 class="font-semibold text-sm text-gray-700">Delivery Notes</h4>
      <span v-if="isFetching" class="text-xs text-gray-500">Loading...</span>
    </div>

    <div v-if="error" class="text-xs text-red-600 mb-2">Failed to load delivery notes.</div>

    <div v-if="notes.length === 0 && !isFetching" class="text-xs text-gray-500">
      No delivery notes found.
    </div>

    <div v-else class="overflow-hidden border border-gray-200 rounded-md">
      <table class="min-w-full bg-white">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-600">Label</th>
            <th class="px-3 py-2 text-right text-xs font-medium text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(note, noteIndex) in notesList" :key="note.id" class="border-t border-gray-100">
            <td class="px-3 py-2 text-sm text-gray-700">
              {{ `Delivery Note #${noteIndex + 1}` }}
            </td>
            <td class="px-3 py-2">
              <div class="flex items-center justify-end gap-2">
                
                <button
                  class="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                  @click="onDownloadPDF(note)"
                  :disabled="isLoading"
                >
                  Download PDF
                </button>
                <button
                  v-if="can('edit-delivery-notes')"
                  class="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                  @click="onEdit(note)"
                  :disabled="isLoading"
                >
                  Edit
                </button>
                <button
                  v-if="can('delete-delivery-notes')"
                  class="px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded border border-red-200"
                  @click="onDelete(note)"
                  :disabled="isLoading"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { can } from '@/composables';
import { showConfirm } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { DeliveryNote } from '@/models';
import { ref } from 'vue';

interface Props {
  order_item_id: number | string;
  isFetching: boolean;
  notesError: string | null;
  notes: DeliveryNote[];
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'edit', note: DeliveryNote): void;
  (e: 'download-pdf', note: DeliveryNote): void;
  (e: 'deleted', id: number | string): void;
}>();

const notesList = ref<DeliveryNote[]>(props.notes ?? []);
const isLoading = ref(false);
const error = ref<any>(null);

const onEdit = (note: DeliveryNote) => {
  emit('edit', note);
};
const onDownloadPDF = (note: DeliveryNote) => {
  emit('download-pdf', note);
};

const onDelete = async (note: DeliveryNote) => {
  if (!note?.id) return;
  const confirmed = await showConfirm(
    'Delete Note',
    'Are you sure you want to delete this delivery note?'
  );
  if (!confirmed) return;
  isLoading.value = true;
  try {
    await http.delete(`/delivery-note/${note.id}`);
    emit('deleted', note.id);
  } catch (err) {
    error.value = err;
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped></style>
